import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useOidc } from '@nohara/react-oidc';

import { useUser } from '../api/get-user';

import { LoadingScreen } from '@/components/ui';
import { paths } from '@/config/paths';

interface UserOnboardingCheckProps {
  children: React.ReactNode;
}

/**
 * A component that checks if the user needs to complete onboarding
 * and redirects them to the onboarding page if they do.
 */
export const UserOnboardingCheck = ({ children }: UserOnboardingCheckProps) => {
  const { userId, isAuthenticated, isLoading: isAuthLoading } = useOidc();
  const { data: user, isLoading: isUserLoading } = useUser(userId);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isLoading = isAuthLoading || isUserLoading;

  // Check if the user needs onboarding
  const needsOnboarding = isAuthenticated && user && !user.name;

  useEffect(() => {
    // If the user is authenticated and needs onboarding, redirect them to the onboarding page
    if (!isLoading && needsOnboarding) {
      navigate(paths.onboarding.getHref());
    }
  }, [navigate, isLoading, needsOnboarding]);

  // Show loading screen while checking user status
  if (isLoading) {
    return <LoadingScreen />;
  }

  // If the user needs onboarding, don't render the children yet
  if (needsOnboarding && pathname !== paths.onboarding.getHref()) {
    return <LoadingScreen />;
  }

  // User doesn't need onboarding, render the children
  return <>{children}</>;
};
